import React from "react";
import { Button, Row } from "reactstrap";
import { Colxx } from "../../components/common/CustomBootstrap";
import { SetIceOrSugar } from "../../helpers/Product";
import classnames from "classnames";
import { AddCommaNumber } from "../../helpers/Utils";
import IntlMessages from "../../helpers/IntlMessages";
import { useState, useEffect } from "react";
import { useMemo } from "react";
import {
  compareSizeNames,
  sortOptionsByPriceAsc,
  sortSizeOptionsByPriceAsc,
} from "../../helpers/ProductOptionSort";
import {
  applySelectedVariantToProduct,
  getVariantQuantity,
  isVariantOutOfStock,
} from "../../helpers/ProductOptionDefaults";

// Helper function để lấy tên thuộc tính tiếng Việt
const getAttributeName = (key) => {
  switch (key) {
    case "size":
      return "Kích thước";
    case "color":
      return "Màu sắc";
    case "material":
      return "Chất liệu";
    default:
      return key.charAt(0).toUpperCase() + key.slice(1);
  }
};

const toNumber = (value) => {
  const parsedValue = parseFloat(value);
  return Number.isNaN(parsedValue) ? 0 : parsedValue;
};

const getVariantOptionPrice = (variants = [], key, optionValue) => {
  const matchedVariant = variants.find(
    (variant) => variant.attributes?.[key]?.value === optionValue,
  );

  if (!matchedVariant) return 0;

  const promoPrice = toNumber(matchedVariant.promo_price);
  if (promoPrice > 0) return promoPrice;

  return toNumber(matchedVariant.price);
};

const getVariantStockText = (variant) => {
  const quantity = getVariantQuantity(variant);
  if (quantity === null) return null;

  return quantity > 0 ? `Còn ${AddCommaNumber(quantity)}` : "Hết hàng";
};

const ContentComponent = ({ product, onSelected, variantOptionsOnly = false, showErrors = false }) => {
  // console.log('product: ', product)
  const [options, setOptions] = useState({});
  const [selectedOptions, setSelectedOptions] = useState({});

  useEffect(() => {
    if (!product || !product.variants || product.variants.length === 0) return;

    const extractedOptions = {};

    // 1. Trích xuất tất cả các tùy chọn vào Map (Giữ nguyên)
    // Bước này thu thập tất cả các loại thuộc tính và các giá trị độc nhất của chúng.
    product.variants.forEach((variant) => {
      if (!variant.attributes) return; // Skip if attributes undefined
      for (const key in variant.attributes) {
        if (!extractedOptions[key]) {
          extractedOptions[key] = new Map();
        }
        const attr = variant.attributes[key];
        if (!extractedOptions[key].has(attr.value)) {
          extractedOptions[key].set(attr.value, attr.label);
        }
      }
    });

    // 2. Lọc bỏ các nhóm thuộc tính chỉ có label rỗng (Bước mới)
    // Duyệt qua các key của object (ví dụ: 'size', 'color', 'material')
    for (const key in extractedOptions) {
      const attributeMap = extractedOptions[key];

      // Lấy tất cả các label từ Map ra một mảng
      const allLabels = Array.from(attributeMap.values());

      // Kiểm tra xem có phải TẤT CẢ các label trong mảng này đều là chuỗi rỗng hoặc null/undefined không
      const allLabelsAreEmpty = allLabels.every(
        (label) => !label || label.trim() === "",
      );

      // Nếu đúng, xóa thuộc tính này khỏi object
      if (allLabelsAreEmpty) {
        delete extractedOptions[key];
      }
    }

    // 3. Chuyển đổi và cập nhật State (Gần như giữ nguyên)
    // Bước này tạo ra cấu trúc dữ liệu cuối cùng để render và quản lý trạng thái.
    const finalOptions = {};
    const initialSelected = {};

    for (const key in extractedOptions) {
      // Chuyển từ Map sang Array để dễ render
      finalOptions[key] = Array.from(
        extractedOptions[key],
        ([value, label]) => ({ label, value }),
      );
      // Khởi tạo trạng thái selected cho các thuộc tính còn lại
      if (
        product.selectedAttributes &&
        product.selectedAttributes[key] &&
        product.selectedAttributes[key].value !== undefined
      ) {
        initialSelected[key] = product.selectedAttributes[key].value;
      } else {
        initialSelected[key] = null;
      }
    }

    setOptions(finalOptions);
    setSelectedOptions(initialSelected);
  }, [product.id, product.variants, product.selectedAttributes]);

  // 2. Hàm xử lý khi người dùng chọn một option
  const handleSelectOption = (key, value, e) => {
    if (e) e.stopPropagation();

    // 1. Calculate new selection state locally
    const isDeselct = selectedOptions[key] === value;
    const newSelectedValue = isDeselct ? null : value;

    const newSelectedOptions = {
      ...selectedOptions,
      [key]: newSelectedValue,
    };

    setSelectedOptions(newSelectedOptions);

    // 2. Derive variant from new state
    const allOptionsSelected = Object.values(newSelectedOptions).every(
      (v) => v !== null,
    );

    let foundVariant = null;
    if (allOptionsSelected && product.variants) {
      foundVariant = product.variants.find((variant) => {
        return Object.keys(newSelectedOptions).every((k) => {
          return variant.attributes[k]?.value === newSelectedOptions[k];
        });
      });
    }

    // 3. Construct updated product object
    // 3. Construct updated product object
    let productToUpdate = { ...product };

    // Always persist current selection state in selectedAttributes
    const updatedSelectedAttributes = {};
    for (const k in newSelectedOptions) {
      if (
        newSelectedOptions[k] !== null &&
        newSelectedOptions[k] !== undefined
      ) {
        // Check if we have a label for this value in our options map
        // options[k] is an array of {label, value}
        const optionLabel = options[k]?.find(
          (opt) => opt.value === newSelectedOptions[k],
        )?.label;
        updatedSelectedAttributes[k] = {
          value: newSelectedOptions[k],
          label: optionLabel,
        };
      }
    }

    if (foundVariant) {
      productToUpdate = applySelectedVariantToProduct(product, foundVariant);
    } else {
      // Partial selection: MUST persist selected attributes so UI doesn't reset
      productToUpdate = {
        ...product,
        product_variant_id: null,
        selectedVariantId: null,
        selectedVariantSku: null,
        selectedVariantQuantity: null,
        selectedVariant: null,
        selectedAttributes: updatedSelectedAttributes,
      };
    }

    onSelected(productToUpdate);
  };

  const getMatchingVariantsForOption = (key, value) => {
    const potentialSelection = { ...selectedOptions, [key]: value };

    return (product?.variants || []).filter((variant) => {
      return Object.keys(potentialSelection).every((optionKey) => {
        if (potentialSelection[optionKey] === null) return true;
        return (
          variant.attributes[optionKey]?.value === potentialSelection[optionKey]
        );
      });
    });
  };

  // 3. Logic để kiểm tra xem một lựa chọn có hợp lệ không (dựa trên các lựa chọn khác)
  const isOptionAvailable = (key, value) => {
    const matchingVariants = getMatchingVariantsForOption(key, value);

    return matchingVariants.some((variant) => !isVariantOutOfStock(variant));
  };

  const getOptionStockText = (key, value) => {
    const matchingVariants = getMatchingVariantsForOption(key, value);
    if (!matchingVariants.length) return null;

    if (matchingVariants.length === 1) {
      return getVariantStockText(matchingVariants[0]);
    }

    const quantities = matchingVariants.map(getVariantQuantity);
    if (quantities.some((quantity) => quantity === null)) return null;

    const totalQuantity = quantities.reduce(
      (total, quantity) => total + quantity,
      0,
    );
    return totalQuantity > 0
      ? `Còn ${AddCommaNumber(totalQuantity)}`
      : "Hết hàng";
  };

  // 4. Tìm variant cuối cùng được chọn và giá của nó
  const finalSelectedVariant = useMemo(() => {
    // Chỉ tìm khi tất cả các option đã được chọn
    const allOptionsSelected = Object.values(selectedOptions).every(
      (v) => v !== null,
    );
    if (!allOptionsSelected) return null;

    return product?.variants?.find((variant) => {
      return Object.keys(selectedOptions).every((key) => {
        return variant.attributes[key]?.value === selectedOptions[key];
      });
    });
  }, [selectedOptions, product?.variants]);

  // TẠO BIẾN GIÁ AN TOÀN
  // Nếu variant có giá > 0, dùng nó. Nếu không, dùng giá của sản phẩm cha.
  const displayPrice =
    finalSelectedVariant && parseFloat(finalSelectedVariant.price) > 0
      ? parseFloat(finalSelectedVariant.price)
      : product.price;

  // 5. Truyền dữ liệu variant đã chọn ra ngoài

  const displayPromoPrice =
    finalSelectedVariant && parseFloat(finalSelectedVariant.promo_price) > 0
      ? parseFloat(finalSelectedVariant.promo_price)
      : product.price_sale
        ? product.price_sale
        : "0";
  const showSale = parseFloat(displayPromoPrice) > 0;
  const sortedVariantOptions = useMemo(() => {
    const sortedOptions = {};

    Object.keys(options).forEach((key) => {
      const currentOptions = options[key] ? [...options[key]] : [];

      if (key === "size") {
        currentOptions.sort((a, b) => {
          const priceDifference =
            getVariantOptionPrice(product?.variants || [], key, a.value) -
            getVariantOptionPrice(product?.variants || [], key, b.value);
          if (priceDifference !== 0) return priceDifference;

          return compareSizeNames(
            a.label || a.value,
            b.label || b.value,
          );
        });
      }

      sortedOptions[key] = currentOptions;
    });

    return sortedOptions;
  }, [options, product?.variants]);

  // 6. Helper to get display title with Retail/Industry fallback
  const getDisplayTitle = (key) => {
    const bt = String(product.business_type || "").toLowerCase().trim();
    const isRetailGroup = ["retail", "grocery", "pharmacy", "electronics"].includes(bt);
    const isCosmeticsGroup = ["cosmetics", "beauty", "spa", "salon"].includes(bt);

    if (key === "size") {
      if (product.sizeTitle) return product.sizeTitle;
      if (bt === "pharmacy") return "Quy cách đóng gói";
      if (bt === "electronics") return "Dung lượng / Kích thước";
      if (isRetailGroup) return "Quy cách";
      return "Kích thước";
    }
    if (key === "color") {
      if (product.colorTitle) return product.colorTitle;
      if (bt === "pharmacy") return "Hàm lượng / Nồng độ";
      if (isRetailGroup) return "Dung tích / Trọng lượng";
      if (isCosmeticsGroup) return "Màu sắc / Mùi hương / Tone";
      return "Màu sắc";
    }
    if (key === "material") {
      if (product.materialTitle) return product.materialTitle;
      if (bt === "pharmacy") return "Hoạt chất chính";
      if (bt === "electronics") return "Thông số kỹ thuật";
      if (isRetailGroup) return "Hương vị";
      return "Chất liệu";
    }
    return getAttributeName(key);
  };

  const handleAttributeSelect = (type, key, value) => {
    let updatedProduct = { ...product };

    if (type === "size" || type === "flavor" || type === "topping") {
      const currentIds = updatedProduct[key] || [];
      // Toggle logic for array
      if (currentIds.includes(value)) {
        updatedProduct[key] = currentIds.filter((id) => id !== value);
      } else {
        // If Size is single select (usually), we might want to replace.
        // But existing code uses array, assuming multiple or single stored as array.
        // Let's assume toggle for now, or check business logic.
        // For 'size', usually single select. For 'topping', multiple.
        if (type === "size" || type === "flavor") {
          updatedProduct[key] = [value]; // Single select for size
        } else {
          updatedProduct[key] = [...currentIds, value]; // Multiple for topping
        }
      }

      // Update names and values on selection for FnB comparison to prevent merging
      if (type === "size") {
        const currentSelectedId = updatedProduct.sizeSelectedId?.[0];
        if (currentSelectedId) {
          const selectedItem = product.size?.find((item) => item.id === currentSelectedId);
          if (selectedItem) {
            updatedProduct.sizeName = selectedItem.name;
            updatedProduct.sizeValue = Number(selectedItem.value);
          }
        } else {
          updatedProduct.sizeName = undefined;
          updatedProduct.sizeValue = undefined;
        }
      } else if (type === "flavor") {
        const currentSelectedId = updatedProduct.flavorSelectedId?.[0];
        if (currentSelectedId) {
          const selectedItem = product.flavor?.find((item) => item.id === currentSelectedId);
          if (selectedItem) {
            updatedProduct.flavorName = selectedItem.name;
            updatedProduct.flavorValue =
              parseFloat(selectedItem.promo_price) > 0
                ? selectedItem.promo_price
                : selectedItem.value;
          }
        } else {
          updatedProduct.flavorName = undefined;
          updatedProduct.flavorValue = undefined;
        }
      } else if (type === "topping") {
        const selectedToppings = (updatedProduct.toppingSelectedId || [])
          .map((id) => product.topping?.find((item) => item.id === id))
          .filter(Boolean);
        updatedProduct.toppingName = selectedToppings.map((item) => item.name);
        updatedProduct.toppingValue = selectedToppings.map((item) =>
          parseFloat(item.promo_price) > 0 ? item.promo_price : item.value
        );
      }
    } else if (type === "ice" || type === "sugar") {
      updatedProduct[key] = value;
    }

    onSelected(updatedProduct);
  };

  const priceType = product.priceType || "retail";

  const bt = String(product.business_type || "").toLowerCase().trim();
  const isFnB = bt === "cafe" || bt === "restaurant";
  const isFood = product.fnb_type === "food";
  const isDrink = isFnB && !isFood;
  const isGuest = window.location.pathname.startsWith("/menu");

  const hasWholesalePrice =
    !isGuest &&
    !isFnB &&
    ((parseFloat(product.price_wholesale) > 0) ||
     (product.variants && product.variants.some(v => parseFloat(v.price_wholesale) > 0)));

  const currentRetailPrice =
    finalSelectedVariant && parseFloat(finalSelectedVariant.price) > 0
      ? (parseFloat(finalSelectedVariant.promo_price) > 0
          ? parseFloat(finalSelectedVariant.promo_price)
          : parseFloat(finalSelectedVariant.price))
      : product.active_sale && parseFloat(product.price_sale) > 0
        ? parseFloat(product.price_sale)
        : parseFloat(product.price) || 0;

  const currentWholesalePrice =
    finalSelectedVariant && parseFloat(finalSelectedVariant.price_wholesale) > 0
      ? parseFloat(finalSelectedVariant.price_wholesale)
      : parseFloat(product.price_wholesale) || 0;

  const handleSelectPriceType = (type) => {
    const updatedProduct = {
      ...product,
      priceType: type,
    };
    onSelected(updatedProduct);
  };

  return (
    <div>
      {/* Price Type Selector */}
      {hasWholesalePrice && (
        <div className="mb-4">
          <div className="mb-2 text-muted font-weight-bold">
            Loại giá áp dụng <span className="text-danger">*</span>
          </div>
          <Row className="mx-0">
            <Colxx xs="6" className="px-1">
              <Button
                type="button"
                size="sm"
                block
                color="primary"
                outline={priceType !== "retail"}
                onClick={() => handleSelectPriceType("retail")}
                className={classnames({
                  "btn-selected": priceType === "retail",
                  "btn-disabled": priceType !== "retail",
                })}
              >
                Bán lẻ: {AddCommaNumber(currentRetailPrice)} đ
              </Button>
            </Colxx>
            <Colxx xs="6" className="px-1">
              <Button
                type="button"
                size="sm"
                block
                color="primary"
                outline={priceType !== "wholesale"}
                onClick={() => handleSelectPriceType("wholesale")}
                className={classnames({
                  "btn-selected": priceType === "wholesale",
                  "btn-disabled": priceType !== "wholesale",
                })}
              >
                Bán sỉ: {AddCommaNumber(currentWholesalePrice)} đ
              </Button>
            </Colxx>
          </Row>
        </div>
      )}

      {Object.keys(sortedVariantOptions).map((key) => {
        const isMissingThisVariant = showErrors && selectedOptions[key] === null;

        return (
          <Row key={key}>
            <div
              className="mb-3 w-100"
              style={isMissingThisVariant ? {
                border: "2px dashed #ffc107",
                padding: "8px 20px",
                borderRadius: "8px",
                backgroundColor: "rgba(255, 193, 7, 0.03)",
              } : null}
            >
              <div className="mb-2 text-muted">
                {getDisplayTitle(key)} <span className="text-danger">* (Bắt buộc)</span>
              </div>
              {sortedVariantOptions[key].map((option, idx) => {
                const isSelected = selectedOptions[key] === option.value;
                const isDisabled = !isOptionAvailable(key, option.value);

                const optionPrice = getVariantOptionPrice(
                  product?.variants || [],
                  key,
                  option.value,
                );
                const optionStockText = getOptionStockText(key, option.value);

                return (
                  <Colxx
                    key={idx}
                    xxs="12"
                    md="12"
                    xl="12"
                    className="mt-1 pad-2px product-btn"
                  >
                    <Button
                      type="button"
                      size="sm"
                      outline
                      block
                      color="primary"
                      onClick={(e) => handleSelectOption(key, option.value, e)}
                      className={classnames({
                        "btn-selected": isSelected,
                        "btn-disabled": !isSelected,
                      })}
                      disabled={isDisabled && !isSelected}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        whiteSpace: "normal",
                        textAlign: "left",
                        width: "100%",
                      }}
                    >
                      <span className="left" style={{ float: "none", marginRight: "8px", flex: 1 }}>{option.label}</span>
                      {/* Chỉ hiển thị giá cho size, không hiển thị cho color và material */}
                      {key === "size" && (
                        <span
                          className="right"
                          style={{ float: "none", whiteSpace: "nowrap", flexShrink: 0 }}
                        >
                          {optionStockText && (
                            <span
                              className={classnames("small mr-1", {
                                "text-white": isSelected,
                                "text-danger":
                                  !isSelected && optionStockText === "Hết hàng",
                                "text-muted":
                                  !isSelected && optionStockText !== "Hết hàng",
                              })}
                            >
                              {optionStockText} ·
                            </span>
                          )}
                          <span>{AddCommaNumber(parseFloat(optionPrice))} đ</span>
                        </span>
                      )}
                      {optionStockText && key !== "size" && (
                        <span
                          className={classnames("right small", {
                            "text-white": isSelected,
                            "text-danger":
                              !isSelected && optionStockText === "Hết hàng",
                            "text-muted":
                              !isSelected && optionStockText !== "Hết hàng",
                          })}
                          style={{ float: "none", whiteSpace: "nowrap", flexShrink: 0 }}
                        >
                          {optionStockText}
                        </span>
                      )}
                    </Button>
                  </Colxx>
                );
              })}
              {isMissingThisVariant && (
                <div className="text-danger small mt-1 font-weight-bold">
                  Vui lòng chọn {getDisplayTitle(key)}
                </div>
              )}
            </div>
          </Row>
        );
      })}

      {!variantOptionsOnly && !product.variants?.length && product.size?.length ? (
        (() => {
          const isMissingSize = showErrors && (!product.sizeSelectedId || product.sizeSelectedId.length === 0);
          return (
            <div
              className="mb-3"
              style={isMissingSize ? {
                border: "2px dashed #ffc107",
                padding: "8px 20px",
                borderRadius: "8px",
                backgroundColor: "rgba(255, 193, 7, 0.03)",
              } : null}
            >
              <div className="mb-2 text-muted">
                {product.sizeTitle ? product.sizeTitle : <IntlMessages id="product.size" />} <span className="text-danger">* (Bắt buộc)</span>
              </div>
              <SizeAndToppingComponent
                data={product.size}
                onSelected={(item) =>
                  handleAttributeSelect("size", "sizeSelectedId", item.id)
                }
                selectedId={product.sizeSelectedId || []}
                name={"sizeSelectedId"}
                product={product}
              />
              {isMissingSize && (
                <div className="text-danger small mt-1 font-weight-bold">
                  Vui lòng chọn kích thước
                </div>
              )}
            </div>
          );
        })()
      ) : null}

      {!variantOptionsOnly && isFnB && product.flavor?.length ? (
        (() => {
          const isMissingFlavor = showErrors && (!product.flavorSelectedId || product.flavorSelectedId.length === 0);
          return (
            <div
              className="mb-3"
              style={isMissingFlavor ? {
                border: "2px dashed #ffc107",
                padding: "8px 20px",
                borderRadius: "8px",
                backgroundColor: "rgba(255, 193, 7, 0.03)",
              } : null}
            >
              <div className="mb-2 text-muted">
                Vị <span className="text-danger">* (Bắt buộc)</span>
              </div>
              <SizeAndToppingComponent
                data={product.flavor}
                name="flavorSelectedId"
                product={product}
                selectedId={product.flavorSelectedId || []}
                onSelected={(item) =>
                  handleAttributeSelect("flavor", "flavorSelectedId", item.id)
                }
              />
              {isMissingFlavor && (
                <div className="text-danger small mt-1 font-weight-bold">
                  Vui lòng chọn vị
                </div>
              )}
            </div>
          );
        })()
      ) : null}

      {!variantOptionsOnly && isFnB && product.topping?.length ? (
        <div className="mb-3">
          <div className="mb-2 text-muted">
            {product.toppingTitle ? product.toppingTitle : <IntlMessages id="product.topping" />} <span className="text-muted small">(Không bắt buộc)</span>
          </div>
          <SizeAndToppingComponent
            data={product.topping}
            name={"toppingSelectedId"}
            product={product}
            selectedId={product.toppingSelectedId || []}
            onSelected={(item) =>
              handleAttributeSelect("topping", "toppingSelectedId", item.id)
            }
          />
        </div>
      ) : null}

      {!variantOptionsOnly && isDrink && product.ice?.length ? (
        <div className="mb-3">
          <div className="mb-2 text-muted">
            {product.iceTitle ? product.iceTitle : <IntlMessages id="product.ice" />} <span className="text-muted small">(Không bắt buộc)</span>
          </div>
          <IceAndSugarComponent
            data={product.ice}
            name={"iceSelectedValue"}
            product={product}
            selectedValue={product.iceSelectedValue}
            onSelected={(item) =>
              handleAttributeSelect("ice", "iceSelectedValue", item.value)
            }
          />
        </div>
      ) : null}

      {!variantOptionsOnly && isDrink && product.sugar?.length ? (
        <div className="mb-3">
          <div className="mb-2 text-muted">
            {product.sugarTitle ? product.sugarTitle : <IntlMessages id="product.sugar" />} <span className="text-muted small">(Không bắt buộc)</span>
          </div>
          <IceAndSugarComponent
            data={product.sugar}
            name={"sugarSelectedValue"}
            product={product}
            selectedValue={product.sugarSelectedValue}
            onSelected={(item) =>
              handleAttributeSelect("sugar", "sugarSelectedValue", item.value)
            }
          />
        </div>
      ) : null}
    </div>
  );
};

const SizeAndToppingComponent = ({
  data,
  name,
  product,
  selectedId,
  onSelected,
}) => {
  return (
    <Row>
      {(name === "sizeSelectedId"
        ? sortSizeOptionsByPriceAsc(data)
        : sortOptionsByPriceAsc(data)
      ).map((_item, idx) => (
        <Colxx
          key={idx}
          xxs="12"
          md="12"
          xl="12"
          className="mt-1 pad-2px product-btn"
        >
          <Button
            type="button"
            size="sm"
            outline
            block
            color="primary"
            onClick={(e) => {
              e.stopPropagation();
              onSelected(_item);
            }}
            className={classnames({
              "btn-selected": selectedId.includes(_item.id),
              "btn-disabled": !selectedId.includes(_item.id),
            })}
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              whiteSpace: "normal",
              textAlign: "left",
              width: "100%",
            }}
          >
            <span className="left" style={{ float: "none", marginRight: "8px", flex: 1 }}>{_item.name}</span>
            <span className="right" style={{ float: "none", whiteSpace: "nowrap", flexShrink: 0 }}>
              {parseFloat(_item.promo_price) > 0 ? (
                <>
                  <del className="mr-1 text-muted small">
                    {AddCommaNumber(_item.value)}
                  </del>
                  <span className="">
                    {_item.alias === "topping" || _item.alias === "flavor"
                      ? `+ `
                      : ""}
                    {AddCommaNumber(_item.promo_price)}
                  </span>
                </>
              ) : (
                <>
                  {_item.alias === "topping" || _item.alias === "flavor"
                    ? `+ ${AddCommaNumber(_item.value)}`
                    : AddCommaNumber(_item.value)}
                </>
              )}{" "}
              đ
            </span>
          </Button>
        </Colxx>
      ))}
    </Row>
  );
};

const IceAndSugarComponent = ({
  data,
  product,
  name,
  selectedValue,
  onSelected,
}) => {
  return (
    <Row>
      {sortOptionsByPriceAsc(data).map((_item, idx) => {
        // Xử lý cả 2 trường hợp: _item là string hoặc object
        const itemValue = typeof _item === "object" ? _item.value : _item;
        const itemName = typeof _item === "object" ? _item.name : _item;

        return (
          <Colxx key={idx} _id={idx} className="mt-1 pad-2px w-20 product-btn">
            <Button
              type="button"
              color="primary"
              size="sm"
              outline
              block
              onClick={(e) => {
                e.stopPropagation();
                onSelected({ value: itemValue });
              }}
              className={classnames({
                "btn-selected": selectedValue === itemValue,
                "btn-disabled": selectedValue !== itemValue,
              })}
            >
              {`${itemName}%`}
            </Button>
          </Colxx>
        );
      })}
    </Row>
  );
};

export default ContentComponent;
